import { Link } from "react-router-dom";
import { Timer, ChevronRight } from "lucide-react";
import { useNow } from "@/components/meeting/RaceHeader";
import { raceMs, clock12, tzLabel, siteDay, splitRaceName } from "@/lib/raceFormat";

const live = (ms, now) => {
  const s = Math.max(0, Math.floor((ms - now) / 1000));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  return h ? `${h}h ${String(m).padStart(2, "0")}m` : `${m}m ${String(sec).padStart(2, "0")}s`;
};

export const nextRace = (meetings, now) => {
  const today = siteDay(now);
  let best = null;
  for (const m of meetings || []) {
    for (const r of m.races || []) {
      const ms = raceMs(r, m.date);
      if (ms && ms > now && siteDay(ms) === today && !r.result && !r.abandoned && (!best || ms < best.ms)) best = { m, r, ms };
    }
  }
  return best;
};

export const NextRaceBanner = ({ meetings }) => {
  const now = useNow(1000);
  const next = nextRace(meetings, now);
  if (!next) return null;
  const { m, r, ms } = next;
  return (
    <Link to={`/meeting/${m.id}?race=${r.number}`} data-testid="next-race-banner"
      className="rise group mb-6 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6 rounded-xl border border-blue-500/40 bg-[#131C31] px-4 py-4 sm:px-5 shadow-[0_0_12px_rgba(37,99,235,0.25)] hover:border-blue-500/70 transition-colors">
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <span className="relative grid place-items-center w-10 h-10 rounded-full bg-blue-600/15 border border-blue-500/40 shrink-0">
          <Timer className="w-5 h-5 text-blue-400" />
          <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse" />
        </span>
        <div className="min-w-0">
          <p className="eyebrow">Next to jump</p>
          <p className="font-display font-bold text-slate-50 truncate" data-testid="next-race-title">
            {m.track} R{r.number}<span className="text-slate-400 font-normal"> · {splitRaceName(r.name || "").title}</span>
          </p>
          <p className="text-xs text-slate-400" data-testid="next-race-time">{clock12(ms)} {tzLabel(ms)} · {m.state}</p>
        </div>
      </div>
      <div className="flex items-center justify-between sm:justify-end gap-4">
        <span className="text-2xl font-bold text-blue-400 tabular-nums" data-testid="next-race-countdown">{live(ms, now)}</span>
        <span className="inline-flex items-center gap-1 rounded-full bg-blue-600 group-hover:bg-blue-700 px-4 py-2 text-sm font-semibold text-white transition-colors" data-testid="next-race-view">
          View race<ChevronRight className="w-4 h-4" />
        </span>
      </div>
    </Link>
  );
};
